import React, { useRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from '@material-ui/core';

import { useIsCurrentUserAdmin, useCustomerHasAccessToPerformance } from 'src/store';
import { useFetchWebsiteAccounts } from 'src/services';
import isURL from 'validator/lib/isURL';
import { WEBSITE_CHANNEL, getChannelLabel } from 'src/data/channel';
import { useReferenceElement } from 'src/hooks';
import { BOOK_DEMO_ORIGIN } from 'src/data/common';
import { UpgradePopover } from 'src/views/UpgradePopover';
import { CustomInput } from 'src/components/CustomInput';
import { ButtonText } from 'src/components/ButtonText';
import ChannelIcon from 'src/components/ChannelIcon';
import { EarthIcon } from 'src/assets/icons/Earth';
import IconDelete from 'src/assets/icons/Delete';
import { BulbIcon } from 'src/assets/icons/Bulb';
import { TooltipPerformanceNotAdmin } from 'src/components/TooltipPerformanceNotAdmin';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';

const INITIAL_STATE = [''];

const prepareUrls = (urls) => urls?.map((url) => ({ id: uniqueId('annotation_'), text: url }));

export const ConnectWebsiteAccountsPopup = ({ open, channel, onSubmit, onDisconnect, onClose }) => {
  const styles = useStyles();

  const buttonRef = useRef();

  const [urls, setUrls] = useState(INITIAL_STATE);

  const isAdmin = useIsCurrentUserAdmin();
  const customerHasAccessToPerformance = useCustomerHasAccessToPerformance();

  const { accounts, selectedIds, loadingAccounts } = useViewState(channel);

  const { referenceElement, setReferenceElement, clearReferenceElement } = useReferenceElement();

  useFetchWebsiteAccounts();

  const connectedAccount = accounts.find((a) => a.id === selectedIds[0]);

  const shouldHaveMultipleUrls = channel === WEBSITE_CHANNEL.landingPages;

  useEffect(() => {
    setUrls(prepareUrls(connectedAccount?.urls || INITIAL_STATE));
  }, [connectedAccount]);

  const hasAnyUrl = urls.some((url) => url.text);

  const label = getChannelLabel(channel).toLowerCase();

  const itemLabel = channel === WEBSITE_CHANNEL.homepage ? label : 'landing page';

  const handleConfirm = useCallback(() => {
    let errors = 0;

    const newList = urls.map((item) => {
      if (item.text && !isURL(item.text)) {
        errors++;
        return { ...item, error: 'Please enter a valid URL' };
      } else {
        return item;
      }
    });

    setUrls(newList);
    if (!!errors) {
      return;
    }
    onSubmit({ urls: urls.map(({ text }) => text), channel });
  }, [channel, urls, onSubmit]);

  const handleUrlChange = useCallback(
    (event, url) => {
      const newList = urls.map((u) =>
        u.id === url.id ? { ...u, text: event.target.value, error: '' } : u
      );

      setUrls(newList);
    },
    [urls]
  );

  const handleAddUrl = useCallback(() => {
    if (customerHasAccessToPerformance) {
      setUrls((prevUrls) => [...prevUrls, { id: uniqueId('url_') }]);
    } else {
      setReferenceElement(buttonRef?.current);
    }
  }, [customerHasAccessToPerformance, setReferenceElement]);

  const handleDeleteUrl = useCallback(
    (urlId) => {
      const newList = urls.filter((url) => url.id !== urlId);
      setUrls(newList);
    },
    [urls]
  );

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={onClose}
      maxWidth="md"
      data-testid="ConnectWebsiteAccountsPopup"
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.title}>
          <ChannelIcon
            className={styles.channelIcon}
            channel={channel}
          />
          Connect your {label}
        </Typography>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Typography className={styles.description}>
          Enter the {itemLabel} you want to connect with Anyword
        </Typography>

        <Typography className={styles.info}>
          <BulbIcon /> For best results, your {itemLabel} should include meaningful marketing
          assets, product descriptions, or company information.
        </Typography>

        {loadingAccounts ? (
          <CircularProgress className="block mx-auto" />
        ) : (
          <>
            <Typography className={styles.label}>Enter website address</Typography>
            {urls.map((url) => (
              <div
                key={url}
                className={styles.itemWrapper}
              >
                <div className={styles.item}>
                  <CustomInput
                    className={styles.url}
                    value={url.text}
                    onChange={(event) => handleUrlChange(event, url)}
                    placeholder="https://www.anyword.com/"
                    startAdornment={
                      <InputAdornment position="start">
                        <EarthIcon className={styles.earthIcon} />
                      </InputAdornment>
                    }
                    error={!!url.error}
                    autoFocus
                  />
                  {urls.length > 1 && (
                    <IconDelete
                      className={styles.deleteIcon}
                      onClick={() => handleDeleteUrl(url.id)}
                    />
                  )}
                </div>
                {url.error && <Typography className={styles.error}>{url.error}</Typography>}
              </div>
            ))}
            {shouldHaveMultipleUrls && (
              <>
                <ButtonText
                  ref={buttonRef}
                  className={styles.addUrlButton}
                  onClick={handleAddUrl}
                  color="primary"
                >
                  Add URL
                </ButtonText>
                <UpgradePopover
                  referenceElement={referenceElement}
                  onClose={clearReferenceElement}
                  origin={BOOK_DEMO_ORIGIN.landingPagesConnectPopup}
                  placement="bottom-start"
                  isBusinessFeature
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        {connectedAccount && (
          <Button
            className={styles.buttonDisconnect}
            onClick={() => onDisconnect(channel)}
            variant="text"
          >
            Disconnect
          </Button>
        )}
        <Button
          className={styles.buttonCancel}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="ConnectWebsiteAccountsPopup__CancelButton"
        >
          Cancel
        </Button>
        <TooltipPerformanceNotAdmin isAdmin={isAdmin}>
          <Button
            className={styles.buttonConfirm}
            onClick={handleConfirm}
            disabled={!isAdmin || !hasAnyUrl}
            variant="contained"
            color="primary"
            aria-label="Confirm"
            data-testid="ConnectWebsiteAccountsPopup__CTAButton"
          >
            Apply
          </Button>
        </TooltipPerformanceNotAdmin>
      </DialogActions>
    </Dialog>
  );
};

ConnectWebsiteAccountsPopup.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
ConnectWebsiteAccountsPopup.defaultProps = {
  className: null,
  open: false,
};
